import { Modal, Input, Button, Upload } from 'antd';
import {
  UploadOutlined,
  SafetyCertificateOutlined,
  CloudServerOutlined,
  IdcardOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import React, { useState, useMemo, useEffect } from 'react';
import { post } from '../../../../services/ApiService';
import toast from 'react-hot-toast';

const { TextArea } = Input;

const IdVerificationModal = ({
  open,
  onClose,
  orderId,
  idCheckSummary,
  customer,
  onSuccess,
}) => {
  const hasHubAttempt = Boolean(idCheckSummary?.hasHubAttempt);
  const [step, setStep] = useState('choose');
  const [method, setMethod] = useState('manual');
  const [customerDob, setCustomerDob] = useState('');
  const [note, setNote] = useState('');
  const [dobError, setDobError] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const maxDob = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  }, []);

  const existingPhoto = customer?.photoIdDocument || idCheckSummary?.photoIdDocument;
  const baseUrl = process.env.REACT_APP_API_ENDPOINT || '';
  const isHubStep = step === 'hub';

  useEffect(() => {
    if (open) {
      setStep(hasHubAttempt ? 'manual' : 'choose');
      setMethod('manual');
    }
  }, [open, hasHubAttempt]);

  const resetForm = () => {
    setStep(hasHubAttempt ? 'manual' : 'choose');
    setMethod('manual');
    setCustomerDob('');
    setNote('');
    setDobError('');
    setPhotoFile(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateDob = () => {
    if (!customerDob) {
      setDobError('');
      return true;
    }
    const todayStr = new Date().toISOString().slice(0, 10);
    if (customerDob >= todayStr) {
      setDobError('Date of birth cannot be today or a future date.');
      return false;
    }
    setDobError('');
    return true;
  };

  const buildFormData = () => {
    const formData = new FormData();
    formData.append('orderId', orderId);
    if (customerDob) formData.append('customerDob', customerDob);
    if (note.trim()) formData.append('note', note.trim());
    if (photoFile) formData.append('photoId', photoFile);
    return formData;
  };

  const veryfyCustomerByStaff = async () => {

    const type = isHubStep ? 'hub' : 'manual';

    if(type === "hub"){
      if (!validateDob()) return;
    }

    setSubmitting(true);
    try {
      const formData = buildFormData();
      formData.append('type', type);

      const res = await post(
        'api/orders/customer/verification',
        formData,
        true,
      );

      if (res.success) {
        if (type === 'hub') {
          if (res.passed) {
            toast.success(res.message || 'Customer verified via Hub');
          } else {
            toast.error(res.message || 'Hub verification failed');
          }
        } else {
          toast.success(res.message || 'Customer marked as ID verified');
        }
        onSuccess?.({ customer: res.customer, idCheckSummary: res.idCheckSummary });
        handleClose();
      } else {
        toast.error(
          res.error ||
            (type === 'hub'
              ? 'Hub verification failed'
              : 'Failed to mark customer as verified'),
        );
      }
    } catch (error) {
      const data = error?.response?.data;
      toast.error(
        data?.error || data?.message || 'An error occurred. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepIndicator = () => {
    if (hasHubAttempt) return null;

    const steps = [
      { key: 'choose', label: '1. Method' },
      { key: 'form', label: '2. Details' },
    ];
    const activeKey = step === 'choose' ? 'choose' : 'form';

    return (
      <div className="id-verify-modal__steps">
        {steps.map((s) => (
          <span
            key={s.key}
            className={[
              'id-verify-modal__step',
              activeKey === s.key && 'id-verify-modal__step--active',
              activeKey === 'form' && s.key === 'choose' && 'id-verify-modal__step--done',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {s.label}
          </span>
        ))}
      </div>
    );
  };

  const renderMethodOption = (value, iconClass, Icon, title, description) => (
    <button
      type="button"
      className={[
        'id-verify-modal__method',
        method === value && 'id-verify-modal__method--selected',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={() => setMethod(value)}
    >
      <span className={`id-verify-modal__method-icon ${iconClass}`}>
        <Icon />
      </span>
      <span className="id-verify-modal__method-body">
        <span className="id-verify-modal__method-title">{title}</span>
        <span className="id-verify-modal__method-desc">{description}</span>
      </span>
      <span className="id-verify-modal__method-check" aria-hidden="true" />
    </button>
  );

  const renderChooseStep = () => (
    <>
      <p className="id-verify-modal__intro">
        Choose how to verify this customer&apos;s identity for this order.
      </p>
      <div className="id-verify-modal__methods">
        {renderMethodOption(
          'manual',
          'id-verify-modal__method-icon--manual',
          SafetyCertificateOutlined,
          'Manual verification',
          'Mark verified after reviewing photo ID. Hub will not be called.',
        )}
        {renderMethodOption(
          'hub',
          'id-verify-modal__method-icon--hub',
          CloudServerOutlined,
          'Hub verification',
          'Run LexisNexis Hub check using customer name and address on file.',
        )}
      </div>
    </>
  );

  const renderVerificationForm = () => (
    <>
      <p className="id-verify-modal__intro">
        {isHubStep
          ? 'Run a LexisNexis Hub identity check. Optionally provide date of birth, photo ID, and a note. If Hub fails, the customer may be sent a photo ID request automatically.'
          : 'Mark the customer as verified after reviewing their photo ID. Hub will not be called.'}
      </p>

      {existingPhoto?.url && !photoFile && (
        <div className="id-verify-modal__photo-card">
          <span className="id-verify-modal__photo-card-label">Existing photo ID on file</span>
          {existingPhoto.url.toLowerCase().endsWith('.pdf') ? (
            <a href={`${baseUrl}${existingPhoto.url}`} target="_blank" rel="noreferrer">
              View PDF — {existingPhoto.filename || 'Document'}
            </a>
          ) : (
            <img
              src={`${baseUrl}${existingPhoto.url}`}
              alt="Photo ID"
            />
          )}
        </div>
      )}

      <div className="id-verify-modal__field">
        <label className="id-verify-modal__label" htmlFor="verifyDob">
          Date of birth <span className="text-muted fw-normal">(optional)</span>
        </label>
        <Input
          type="date"
          id="verifyDob"
          value={customerDob}
          max={maxDob}
          onChange={(e) => {
            setCustomerDob(e.target.value);
            if (dobError) setDobError('');
          }}
          status={dobError ? 'error' : undefined}
        />
        {dobError && <span className="id-verify-modal__error">{dobError}</span>}
      </div>

      <div className="id-verify-modal__field">
        <label className="id-verify-modal__label">Photo ID <span className="text-muted fw-normal">(optional)</span></label>
        <Upload
          className="id-verify-modal__upload"
          accept=".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf"
          maxCount={1}
          beforeUpload={(file) => {
            setPhotoFile(file);
            return false;
          }}
          onRemove={() => setPhotoFile(null)}
          fileList={
            photoFile
              ? [{ uid: '-1', name: photoFile.name, status: 'done' }]
              : []
          }
        >
          <Button icon={<UploadOutlined />} className="id-verify-modal__upload-btn">
            {photoFile ? 'Replace file' : 'Select JPEG, PNG, WebP or PDF (max 5MB)'}
          </Button>
        </Upload>
      </div>

      <div className="id-verify-modal__field">
        <label className="id-verify-modal__label">Note <span className="text-muted fw-normal">(optional)</span></label>
        <TextArea
          rows={3}
          placeholder={
            isHubStep
              ? 'e.g. DOB taken from driving licence'
              : 'e.g. Passport verified via eBay message'
          }
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
    </>
  );

  const getTitleIcon = () => {
    if (step === 'hub') return CloudServerOutlined;
    if (step === 'manual') return SafetyCertificateOutlined;
    return IdcardOutlined;
  };

  const getTitleText = () => {
    if (step === 'choose') return 'Verify customer ID';
    if (step === 'hub') return 'Hub verification';
    return 'Manual ID verification';
  };

  const TitleIcon = getTitleIcon();

  const renderFooter = () => {
    if (step === 'choose') {
      return [
        <Button key="cancel" onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>,
        <Button
          key="next"
          type="primary"
          onClick={() => setStep(method === 'hub' ? 'hub' : 'manual')}
        >
          Continue
        </Button>,
      ];
    }

    if (step === 'hub') {
      return [
        !hasHubAttempt && (
          <Button
            key="back"
            icon={<ArrowLeftOutlined />}
            onClick={() => setStep('choose')}
            disabled={submitting}
          >
            Back
          </Button>
        ),
        <Button key="cancel" onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          icon={<CloudServerOutlined />}
          onClick={veryfyCustomerByStaff}
          loading={submitting}
        >
          Run Hub verification
        </Button>,
      ].filter(Boolean);
    }

    return [
      !hasHubAttempt && (
        <Button
          key="back"
          icon={<ArrowLeftOutlined />}
          onClick={() => setStep('choose')}
          disabled={submitting}
        >
          Back
        </Button>
      ),
      <Button key="cancel" onClick={handleClose} disabled={submitting}>
        Cancel
      </Button>,
      <Button
        key="submit"
        type="primary"
        icon={<CheckCircleOutlined />}
        onClick={veryfyCustomerByStaff}
        loading={submitting}
      >
        Confirm verification
      </Button>,
    ].filter(Boolean);
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={
        <div className="id-verify-modal__title">
          <span className="id-verify-modal__title-icon">
            <TitleIcon />
          </span>
          {getTitleText()}
        </div>
      }
      footer={renderFooter()}
      width={520}
      className="id-verify-modal"
      destroyOnHidden
      maskClosable={!submitting}
      closable={!submitting}
    >
      {renderStepIndicator()}
      {step === 'choose' && renderChooseStep()}
      {(step === 'manual' || step === 'hub') && renderVerificationForm()}
    </Modal>
  );
};

export default IdVerificationModal;
